import { Grid, Typography, useTheme } from '@mui/material';
import { Answer, Perspectives, Question } from 'features/lead/lead-questionnaire/lead-questionnaire.types';
import PaiRadioItem from 'features/ui/base-components/pai-radio-item';
import PaiTextArea from 'features/ui/base-components/pai-text-area';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  perspective?: string;
  question: Question;
  questions: Question[];
  index: number;
  questionType: string;

  answers: Answer[];

  handleRadioSelectionChange?(id: string): (value: string | number) => () => void;

  handleOpenTextFieldChange?(id: string): (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const QuestionItem: React.FC<Props> = ({
  perspective,
  question,
  questions,
  index,
  questionType,
  answers,
  handleRadioSelectionChange,
  handleOpenTextFieldChange,
}) => {
  const { classes } = useStyles({ questionType });
  const { t } = useTranslation();
  const theme = useTheme();

  const [answerObject, setAnswerObject] = useState<Answer>(questionType === 'closed' ? { id: '', value: 0 } : { id: '', value: '' });

  useEffect(() => {
    const findCurrent = answers.find((answer) => answer.id === question.id);
    if (findCurrent) {
      setAnswerObject(findCurrent);
    }
  }, [answers]);

  const renderOptions = () => {
    switch (questionType) {
      case 'open':
        return !_.isNil(handleOpenTextFieldChange) ? (
          <PaiTextArea
            value={`${answerObject.value}`}
            placeholder={t('enterYourText')}
            placeholderSize={13}
            onChange={handleOpenTextFieldChange(question.id)}
            rows={10}
            disableBorder={true}
            backgroundColor={'#FFF'}
          />
        ) : null;
      case 'closed':
        return !_.isNil(handleRadioSelectionChange) ? (
          <Grid container={true} className={classes.answerRow} justifyContent={'space-around'}>
            <Grid size={1} />
            <Grid className={classes.radioContainer} size={1}>
              <PaiRadioItem value={1} selectedValue={answerObject.value} handleSelectItem={handleRadioSelectionChange(question.id)} />
            </Grid>
            <Grid className={classes.radioContainer} size={1}>
              <PaiRadioItem value={2} selectedValue={answerObject.value} handleSelectItem={handleRadioSelectionChange(question.id)} />
            </Grid>
            <Grid className={classes.radioContainer} size={1}>
              <PaiRadioItem value={3} selectedValue={answerObject.value} handleSelectItem={handleRadioSelectionChange(question.id)} />
            </Grid>
            <Grid className={classes.radioContainer} size={1}>
              <PaiRadioItem value={4} selectedValue={answerObject.value} handleSelectItem={handleRadioSelectionChange(question.id)} />
            </Grid>
            <Grid className={classes.radioContainer} size={1}>
              <PaiRadioItem value={5} selectedValue={answerObject.value} handleSelectItem={handleRadioSelectionChange(question.id)} />
            </Grid>
          </Grid>
        ) : null;
      default:
        return undefined;
    }
  };

  return (
    <Grid
      className={classes.questionContainer}
      container={true}
      alignItems={questionType === 'open' ? 'flex-start' : 'center'}
      style={{
        borderBottom: index !== questions.length - 1 && questionType !== 'open' ? `1px solid ${theme.palette.greys.medium}` : undefined,
      }}
    >
      {questionType !== 'open' && (
        <Grid size={4}>
          <Typography className={classes.question}>
            {perspective === Perspectives.SELF ? question.selfAssessment : question.externalAssessment}
          </Typography>
        </Grid>
      )}
      <Grid size={questionType === 'open' ? 12 : 8}>
        {renderOptions()}
      </Grid>
    </Grid>
  );
};

const useStyles = makeStyles<{ questionType: string }>()((theme, { questionType }) => ({
  question: {
    fontSize: theme.typography.pxToRem(14),
  },
  questionContainer: {
    padding: questionType === 'open' ? '16px' : '8px 48px 8px 16px',
    minHeight: '60px',
  },
  answerRow: {
    width: '100%',
  },
  radioContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
}));

export default QuestionItem;
